import { Link } from '@tanstack/react-router'
import type { Product } from '#bv/data/products'
import { ProductPurchaseActions } from '#bv/components/ProductPurchaseActions'
import { DisplayPrice } from '#bv/components/DisplayPrice'
import { FeatureIcon, StarRatingPink } from '#bv/components/FeatureIcons'
import { StockAvailabilityBadge } from '#bv/components/StockAvailabilityBadge'
import { useCurrency } from '#bv/lib/currency/CurrencyContext'
import { useProductStock } from '#bv/lib/useProductStock'
import { productCardBlurb } from '#bv/lib/productCardCopy'

type Props = {
  product: Product
  catalogBrand: string
  checkoutName: string
  unitPrice: number
  familyCtx: {
    familyName: string
    hubId: number
  } | null
  familyTierLabel: string | null
}

export function StandardProductPanel({
  product,
  catalogBrand,
  checkoutName,
  unitPrice,
  familyCtx,
  familyTierLabel,
}: Props) {
  const { inStock } = useProductStock()
  const { price: displayPrice } = useCurrency()
  const productInStock = inStock(product.id)
  const ratingText = product.rating.toFixed(1)
  const rawBlurb = productCardBlurb(product) ?? product.shortDescription
  const listingBlurb = rawBlurb
    ? (/capcut/i.test(product.name)
      ? rawBlurb.replace(/\$\d+\.?\d*/g, "")
      : rawBlurb.replace(/\$\d+\.?\d*/g, (match) => displayPrice(parseFloat(match)).formatted)
    )
    : rawBlurb
  const isLowStock = product.stock <= 15
  const showCompare =
    product.compareAtPrice != null && product.compareAtPrice > unitPrice + 0.004

  return (
    <div className="pc-pdp-panel">
      <p className="pc-pdp-panel__kicker">
        {catalogBrand}
        {familyTierLabel ? ` · ${familyTierLabel}` : ''}
      </p>
      <h1 className="pc-pdp-panel__title font-display chrome-text">{product.name}</h1>

      {/\bshared access\b/i.test(product.name) ? (
        <p className="pc-pdp-panel__warn">
          <span className="font-semibold text-[#d4e8ff]">Shared access:</span> duration not guaranteed.
        </p>
      ) : null}

      {/capcut/i.test(product.name) ? (
        <div className="pc-pdp-panel__warn" style={{ borderColor: 'rgba(251,191,36,0.25)', background: 'rgba(120,53,15,0.2)' }}>
          <p className="m-0 text-xs leading-relaxed text-[#8aa0c4]">
            <span className="font-semibold text-[#fbbf24]">Important notice:</span>{' '}
            You will receive a CapCut Pro account. Please note that some accounts are optimized for browser use and may not function properly inside the desktop or mobile application. If you encounter compatibility issues with the app, you must use the web browser version. A working account that functions via browser does not qualify for a replacement or refund.
          </p>
        </div>
      ) : null}

      <div className="pc-pdp-panel__rating-pill">
        <StarRatingPink rating={product.rating} size={16} />
        <span className="font-bold tabular-nums text-[#00bfff]">{ratingText}</span>
        <span className="pc-pdp-panel__rating-sep" aria-hidden />
        <span className="pc-pdp-panel__rating-count">({product.reviewCount.toLocaleString()} reviews)</span>
      </div>

      {listingBlurb ? <p className="pc-pdp-panel__blurb">{listingBlurb}</p> : null}

      {familyCtx ? (
        <Link
          to="/products/$productId"
          params={{ productId: String(familyCtx.hubId) }}
          search={{ tier: product.id }}
          className="pc-pdp-panel__family-link"
        >
          More {familyCtx.familyName} tiers →
        </Link>
      ) : null}

      <div className="pc-pdp-panel__price-section">
        <p className="pc-pdp-panel__price-label">Price</p>
        <div className="pc-pdp-panel__price-row">
          {showCompare ? (
            <DisplayPrice usd={product.compareAtPrice!} className="pc-pdp-panel__was" />
          ) : null}
          <span className="pc-pdp-panel__price font-display">{displayPrice(unitPrice).formatted}</span>
        </div>
      </div>

      <div className="pc-pdp-panel__stock-row">
        <StockAvailabilityBadge inStock={productInStock} size="comfortable" />
        {productInStock && isLowStock ? (
          <span className="pc-pdp-panel__low">
            <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-cyan-400" aria-hidden />
            Only {product.stock} left
          </span>
        ) : null}
      </div>

      <div className="pc-pdp-panel__actions">
        <ProductPurchaseActions productId={product.id} name={checkoutName} unitPrice={unitPrice} />
      </div>

      <p className="pc-pdp-panel__fees">Payment processor and store fees may apply at checkout.</p>

      <div className="pc-pdp-panel__delivery">
        <FeatureIcon name="zap" size={20} className="mt-0.5 shrink-0 text-[#fbbf24]" aria-hidden />
        <div className="min-w-0">
          <p className="pc-pdp-panel__delivery-kicker">After checkout</p>
          <p className="pc-pdp-panel__delivery-text">{product.deliveryInfo}</p>
        </div>
      </div>
    </div>
  )
}
